import React, { useCallback, useEffect, useState } from 'react';
import { missionControlService } from '../../../services/missionControl.js';
import type { MilestoneConfig } from '../../../services/missionControl.js';
import './milestone-settings.css';

const MANUAL_KEYS = new Set(['arr', 'qoq_growth']);

interface MilestoneSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MilestoneSettings({
  isOpen,
  onClose,
}: MilestoneSettingsProps): React.ReactElement | null {
  const [configs, setConfigs] = useState<MilestoneConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set());

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await missionControlService.getMilestoneConfigs();
      if (result.success && result.data) {
        setConfigs(result.data);
      } else {
        setError('Failed to load milestone configurations.');
      }
    } catch {
      setError('Failed to load milestone configurations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      void fetchConfigs();
    }
  }, [isOpen, fetchConfigs]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  async function handleUpdate(
    id: string,
    field: 'target' | 'weight' | 'manualCurrent',
    value: number | null,
    currentConfig: MilestoneConfig
  ) {
    setUpdatingIds((prev) => new Set(prev).add(id));
    const oldValue =
      field === 'target'
        ? currentConfig.target
        : field === 'weight'
          ? currentConfig.weight
          : currentConfig.manualCurrent;

    // Optimistic update
    setConfigs((prev) => prev.map((c) => (c.id === id ? { ...c, [field]: value } : c)));

    try {
      const result = await missionControlService.updateMilestoneConfig(id, { [field]: value });
      if (!result.success) {
        setConfigs((prev) => prev.map((c) => (c.id === id ? { ...c, [field]: oldValue } : c)));
      }
    } catch {
      setConfigs((prev) => prev.map((c) => (c.id === id ? { ...c, [field]: oldValue } : c)));
    } finally {
      setUpdatingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }

  const weightTotal = configs.reduce((sum, c) => sum + c.weight, 0);
  const weightOk = Math.abs(weightTotal - 1) < 0.001;

  return (
    <>
      <div
        className={`mc-settings-backdrop${isOpen ? ' mc-settings-backdrop--open' : ''}`}
        onClick={onClose}
        data-testid="ms-settings-backdrop"
      />
      <div
        className={`mc-settings-drawer${isOpen ? ' mc-settings-drawer--open' : ''}`}
        role="dialog"
        aria-label="Milestone Settings"
      >
        <div className="mc-settings__glow" />
        <div className="mc-settings__header">
          <div className="mc-settings__title-group">
            <div className="mc-settings__title-bar" />
            <h3 className="mc-settings__title">Milestone Settings</h3>
          </div>
          <button
            className="mc-settings__close-btn"
            onClick={onClose}
            aria-label="Close milestone settings"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M1 1l12 12M13 1L1 13"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {error && <div className="mc-settings__error">{error}</div>}

        <div className="mc-settings__body">
          {loading ? (
            <div className="mc-settings__loading">Loading milestones...</div>
          ) : configs.length === 0 ? (
            <div className="mc-settings__empty">No milestone configurations found.</div>
          ) : (
            <>
              {configs.map((cfg) => (
                <MilestoneRow
                  key={cfg.id}
                  config={cfg}
                  updating={updatingIds.has(cfg.id)}
                  onUpdate={handleUpdate}
                />
              ))}
              <div className={`mc-ms-weight-total${weightOk ? '' : ' mc-ms-weight-total--warn'}`}>
                <span className="mc-ms-weight-total__label">TOTAL WEIGHT</span>
                <span className="mc-ms-weight-total__value">{(weightTotal * 100).toFixed(0)}%</span>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ── Milestone Row ──────────────────────────────────────

interface MilestoneRowProps {
  config: MilestoneConfig;
  updating: boolean;
  onUpdate: (
    id: string,
    field: 'target' | 'weight' | 'manualCurrent',
    value: number | null,
    currentConfig: MilestoneConfig
  ) => void;
}

function MilestoneRow({ config, updating, onUpdate }: MilestoneRowProps): React.ReactElement {
  const [localTarget, setLocalTarget] = useState(String(config.target));
  const [localWeight, setLocalWeight] = useState(String(config.weight * 100));
  const [localCurrent, setLocalCurrent] = useState(
    config.manualCurrent !== null ? String(config.manualCurrent) : ''
  );

  // Sync local state when config changes from outside
  useEffect(() => {
    setLocalTarget(String(config.target));
    setLocalWeight(String(config.weight * 100));
    setLocalCurrent(config.manualCurrent !== null ? String(config.manualCurrent) : '');
  }, [config.target, config.weight, config.manualCurrent]);

  function commitTarget() {
    const val = parseFloat(localTarget);
    if (!isNaN(val) && val > 0 && val !== config.target) {
      onUpdate(config.id, 'target', val, config);
    } else {
      setLocalTarget(String(config.target));
    }
  }

  function commitWeight() {
    const val = parseFloat(localWeight) / 100;
    if (!isNaN(val) && val >= 0 && val <= 1 && val !== config.weight) {
      onUpdate(config.id, 'weight', val, config);
    } else {
      setLocalWeight(String(config.weight * 100));
    }
  }

  function commitCurrent() {
    const val = localCurrent.trim() === '' ? null : parseFloat(localCurrent);
    if (val !== null && isNaN(val)) {
      setLocalCurrent(config.manualCurrent !== null ? String(config.manualCurrent) : '');
      return;
    }
    if (val !== config.manualCurrent) {
      onUpdate(config.id, 'manualCurrent', val, config);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent, commitFn: () => void) {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
      commitFn();
    }
  }

  const isManual = MANUAL_KEYS.has(config.key);

  return (
    <div className="mc-ms-row">
      <div className="mc-ms-row__header">
        <span className="mc-ms-row__label">{config.label}</span>
        <span className="mc-ms-row__weight-badge">{(config.weight * 100).toFixed(0)}%</span>
      </div>
      <div className="mc-ms-row__fields">
        {isManual ? (
          <div className="mc-ms-field">
            <label className="mc-ms-field__label">Current</label>
            <input
              className="mc-ms-field__input"
              type="number"
              value={localCurrent}
              onChange={(e) => setLocalCurrent(e.target.value)}
              onBlur={commitCurrent}
              onKeyDown={(e) => handleKeyDown(e, commitCurrent)}
              disabled={updating}
              aria-label={`Current value for ${config.label}`}
            />
          </div>
        ) : (
          <div className="mc-ms-field">
            <label className="mc-ms-field__label">Current</label>
            <span className="mc-ms-field__live">LIVE</span>
          </div>
        )}
        <div className="mc-ms-field">
          <label className="mc-ms-field__label">Target</label>
          <input
            className="mc-ms-field__input"
            type="number"
            value={localTarget}
            onChange={(e) => setLocalTarget(e.target.value)}
            onBlur={commitTarget}
            onKeyDown={(e) => handleKeyDown(e, commitTarget)}
            disabled={updating}
            aria-label={`Target for ${config.label}`}
          />
        </div>
        <div className="mc-ms-field">
          <label className="mc-ms-field__label">Weight</label>
          <div className="mc-ms-field__input-group">
            <input
              className="mc-ms-field__input mc-ms-field__input--short"
              type="number"
              value={localWeight}
              onChange={(e) => setLocalWeight(e.target.value)}
              onBlur={commitWeight}
              onKeyDown={(e) => handleKeyDown(e, commitWeight)}
              disabled={updating}
              aria-label={`Weight for ${config.label}`}
            />
            <span className="mc-ms-field__suffix">%</span>
          </div>
        </div>
      </div>
    </div>
  );
}
